/** Local census floor: auto peers should turn on below this many visible archivists. */
export const ARCHIVIST_TARGET_FLOOR = 8;
/** Local census ceiling: auto peers turn off at/above this many (hysteresis band). */
export const ARCHIVIST_TARGET_CEILING = 12;

export const ARCHIVIST_PREFERENCES = Object.freeze(['auto', 'on', 'off']);

/**
 * Resolve whether this peer should run archivist duty.
 * `visibleArchivistCount` is self (if currently on) + lobby peers advertising archivist.
 * Dead band [FLOOR, CEILING) keeps the previous auto state to avoid flapping.
 */
export function resolveArchivistEnabled({
  preference = 'auto',
  constrained = false,
  visibleArchivistCount = 0,
  previouslyAutoOn = false
} = {}) {
  if (preference === 'on') {
    return true;
  }
  if (preference === 'off') {
    return false;
  }
  if (constrained) {
    return false;
  }
  const count = Math.max(0, Number(visibleArchivistCount) || 0);
  if (count < ARCHIVIST_TARGET_FLOOR) {
    return true;
  }
  if (count >= ARCHIVIST_TARGET_CEILING) {
    return false;
  }
  return Boolean(previouslyAutoOn);
}

/** Count lobby peers that advertise archivist duty (excludes self). */
export function countPeerArchivists(peers = []) {
  return (Array.isArray(peers) ? peers : []).filter((peer) => (
    peer?.archivist === true
    || peer?.metadata?.archivist === true
  )).length;
}

/**
 * Visible census used by the resolver: other archivists + 1 if self is currently on.
 */
export function countVisibleArchivists(peers = [], { selfEnabled = false } = {}) {
  return countPeerArchivists(peers) + (selfEnabled ? 1 : 0);
}

export function normalizeArchivistPreference(value) {
  const next = String(value || '').toLowerCase();
  return ARCHIVIST_PREFERENCES.includes(next) ? next : 'auto';
}

/**
 * Migrate legacy `{ enabled }` storage and normalize current shape.
 * Legacy enabled:true → preference on; missing/false → preference auto.
 */
export function migrateArchivistSettings(parsed, { allShards = [] } = {}) {
  const shardsForOn = Array.isArray(allShards) && allShards.length > 0
    ? allShards
    : [];

  if (!parsed || typeof parsed !== 'object') {
    return { preference: 'auto', enabled: false, shards: [] };
  }

  if (parsed.preference != null) {
    const preference = normalizeArchivistPreference(parsed.preference);
    const enabled = preference === 'on'
      ? true
      : preference === 'off'
        ? false
        : Boolean(parsed.enabled);
    return {
      preference,
      enabled,
      shards: enabled ? shardsForOn : []
    };
  }

  if (parsed.enabled) {
    return {
      preference: 'on',
      enabled: true,
      shards: shardsForOn
    };
  }

  return { preference: 'auto', enabled: false, shards: [] };
}

export function archivistSettingsPayload(preference, enabled, allShards = []) {
  const normalized = normalizeArchivistPreference(preference);
  const nextEnabled = normalized === 'on'
    ? true
    : normalized === 'off'
      ? false
      : Boolean(enabled);
  return {
    preference: normalized,
    enabled: nextEnabled,
    shards: nextEnabled ? allShards : []
  };
}
